import { useCallback, useEffect, useRef } from "react";

import { SITEKEYCAPTCHA } from "@/recourses/constants";
import { getProjectWindow, injectScript, removeScript } from "@/recourses/captchaV3";

const SCRIPT_ID = "google-recaptcha-script";
const ERROR_SCRIPT_NOT_AVAILABLE = "Google reCAPTCHA is not available";
const ERROR_NOT_A_WEB_BROWSER = "Running outside a web browser";

let scriptRefCount = 0;

interface IGoogleReCaptchaOptions {
  action?: string;
  siteKey?: string;
  shouldLoad?: boolean;
  asynch?: boolean;
}

interface IGoogleReCaptchaV3HookReturn {
  executeReCaptcha: (action?: string) => Promise<string>;
}

type TGoogleReCaptchaV3Hook = (options?: IGoogleReCaptchaOptions) => IGoogleReCaptchaV3HookReturn;

const useGoogleReCaptchaV3: TGoogleReCaptchaV3Hook = (options = {}) => {
  const { action = "load", siteKey = SITEKEYCAPTCHA, shouldLoad = false, asynch = false } = options;

  const isMountedRef = useRef(true);

  const executeReCaptcha = useCallback(
    async (action: string = "default"): Promise<string> => {
      const window = getProjectWindow();

      if (!window) {
        throw new Error(ERROR_NOT_A_WEB_BROWSER);
      }

      const { grecaptcha } = window as any;
      if (!grecaptcha) {
        throw new Error(ERROR_SCRIPT_NOT_AVAILABLE);
      }

      return await new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha
            .execute(siteKey, { action })
            .then((token: string) => resolve(token))
            .catch((error: Error) => reject(error));
        });
      });
    },
    [siteKey]
  );

  useEffect(() => {
    isMountedRef.current = true;

    if (!siteKey) return;
    if (!shouldLoad) return;

    const window = getProjectWindow();
    if (window) {
      const scriptTag = window.document.getElementById(SCRIPT_ID);

      if (!scriptTag) {
        injectScript(SCRIPT_ID, `https://www.google.com/recaptcha/api.js?render=${siteKey}`);
        const badge = document.querySelector(".grecaptcha-badge");
        badge?.setAttribute("style", "visibility: hidden");
      }

      scriptRefCount++;
    }

    return () => {
      isMountedRef.current = false;
      scriptRefCount--;

      // Only remove script when no components are using it
      if (scriptRefCount === 0) {
        removeScript(SCRIPT_ID);
      }
    };
  }, [siteKey, action, asynch, shouldLoad]);

  return {
    executeReCaptcha,
  };
};

export default useGoogleReCaptchaV3;
